import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import PolygonEditor from '../../../../components/maps/PolygonEditor';
import client from '../../../../lib/feathers';

const DESIGNATION_TYPES = [
  { value: 'ava', label: 'AVA (American Viticultural Area)' },
  { value: 'aoc', label: 'AOC (Appellation d\'Origine Controlee)' },
  { value: 'aop', label: 'AOP (Appellation d\'Origine Protegee)' },
  { value: 'doc', label: 'DOC (Denominazione di Origine Controllata)' },
  { value: 'docg', label: 'DOCG (Denominazione di Origine Controllata e Garantita)' },
  { value: 'do', label: 'DO (Denominacion de Origen)' },
  { value: 'doca', label: 'DOCa (Denominacion de Origen Calificada)' },
  { value: 'gi', label: 'GI (Geographical Indication)' },
  { value: 'wo', label: 'WO (Wine of Origin)' },
  { value: 'vqa', label: 'VQA (Vintners Quality Alliance)' },
  { value: 'ig', label: 'IG (Indicacao Geografica)' },
];

export default function AppellationEdit() {
  const { aid } = useParams();
  const isNew = !aid;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    designationType: 'ava',
    country: 'US',
    parentId: null,
    active: true,
    meta: null,
  });
  const [boundary, setBoundary] = useState(null);
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [parentName, setParentName] = useState('');
  const [showParentSearch, setShowParentSearch] = useState(false);
  const [referenceBoundaries, setReferenceBoundaries] = useState([]);

  const loadAppellation = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/appellation');
      const result = await service.get(aid);
      setFormData({
        name: result.name || '',
        shortName: result.shortName || '',
        designationType: result.designationType || 'ava',
        country: result.country || 'US',
        parentId: result.parentId || null,
        active: result.active !== false,
        meta: result.meta || null,
      });
      if (result.boundary) {
        setBoundary(result.boundary);
      }
      if (result.parentId) {
        try {
          const parent = await service.get(result.parentId);
          setParentName(parent.name);
        } catch { /* parent may not exist */ }
      }
    } catch (e) {
      console.error('Error loading appellation:', e);
      setError('Failed to load appellation.');
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceBoundaries = useCallback(async () => {
    try {
      const service = client.service('admin/appellation');
      const q = {
        $limit: 50,
        $sort: { name: 1 },
        boundary: { $ne: null },
      };
      if (formData.parentId) {
        q.parentId = formData.parentId;
      }
      const result = await service.find({ query: q });
      setReferenceBoundaries(
        result.data
          .filter(a => a.id !== aid && a.boundary)
          .map(a => ({
            id: a.id,
            name: a.name,
            boundary: a.boundary,
          }))
      );
    } catch {
      // Non-critical
    }
  }, [aid, formData.parentId]);

  const searchParents = async (query) => {
    if (!query || query.length < 2) {
      setParentResults([]);
      return;
    }
    try {
      const service = client.service('admin/appellation');
      const result = await service.find({
        query: {
          name: { $iLike: `%${query}%` },
          $limit: 10,
          $sort: { name: 1 },
        }
      });
      setParentResults(result.data.filter(a => a.id !== aid));
    } catch {
      setParentResults([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Name is required.');
      return;
    }

    try {
      setSaving(true);
      const service = client.service('admin/appellation');
      const payload = { ...formData };

      if (boundary) {
        payload.boundary = boundary;
      } else {
        payload.boundary = null;
      }

      if (isNew) {
        const result = await service.create(payload);
        navigate(`/admin/appellations/${result.id}`);
      } else {
        await service.patch(aid, payload);
        navigate(`/admin/appellations/${aid}`);
      }
    } catch (err) {
      console.error('Error saving appellation:', err);
      alert(err.message || 'Failed to save appellation.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isNew) {
      loadAppellation();
    }
  }, [aid]);

  useEffect(() => {
    loadReferenceBoundaries();
  }, [loadReferenceBoundaries]);

  if (loading) {
    return (
      <Page title="Loading...">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Error">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </Page>
    );
  }

  const breadcrumbElements = [
    { name: 'Appellations', href: '/admin/appellations' },
  ];
  if (!isNew) {
    breadcrumbElements.push({ name: formData.name, href: `/admin/appellations/${aid}` });
    breadcrumbElements.push({ name: 'Edit' });
  } else {
    breadcrumbElements.push({ name: 'New Appellation' });
  }

  return (
    <Page title={isNew ? 'New Appellation' : 'Edit Appellation'}>
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={breadcrumbElements} />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        {/* Metadata */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Appellation Details</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Willamette Valley"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="shortName" className="block text-sm font-medium text-gray-700">
                Short Name
              </label>
              <input
                type="text"
                id="shortName"
                value={formData.shortName}
                onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                placeholder="Optional abbreviation"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="designationType" className="block text-sm font-medium text-gray-700">
                  Designation Type *
                </label>
                <select
                  id="designationType"
                  value={formData.designationType}
                  onChange={(e) => setFormData({ ...formData, designationType: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {DESIGNATION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  Country (ISO 3166-1 alpha-2) *
                </label>
                <input
                  type="text"
                  id="country"
                  required
                  maxLength={2}
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase() })}
                  placeholder="US"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            {/* Parent Appellation Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Parent Appellation
              </label>
              {formData.parentId ? (
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-sm text-gray-900">{parentName || formData.parentId}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, parentId: null });
                      setParentName('');
                    }}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <input
                    type="text"
                    value={parentSearch}
                    onChange={(e) => {
                      setParentSearch(e.target.value);
                      setShowParentSearch(true);
                      searchParents(e.target.value);
                    }}
                    onFocus={() => setShowParentSearch(true)}
                    placeholder="Search for parent appellation..."
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                  {showParentSearch && parentResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-48 overflow-y-auto">
                      {parentResults.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, parentId: p.id });
                            setParentName(p.name);
                            setParentSearch('');
                            setShowParentSearch(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          {p.name} <span className="text-gray-400">({p.designationType.toUpperCase()}, {p.country})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="active"
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="active" className="font-medium text-gray-700">Active</label>
                <p className="text-gray-500">Inactive appellations are excluded from auto-assignment</p>
              </div>
            </div>
          </div>
        </div>

        {/* Boundary Editor */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Boundary</h3>
          <p className="text-sm text-gray-500 mb-4">
            Draw or import a polygon boundary. Listings within this boundary will be automatically assigned to this appellation.
          </p>
          <PolygonEditor
          referenceLabel="Gray dashed outlines are sibling appellations."
          downloadName="appellation-boundary.geojson"
            boundary={boundary}
            onBoundaryChange={setBoundary}
            referenceBoundaries={referenceBoundaries}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(isNew ? '/admin/appellations' : `/admin/appellations/${aid}`)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {saving ? 'Saving...' : (isNew ? 'Create Appellation' : 'Save Changes')}
          </button>
        </div>
      </form>
    </Page>
  );
}
